import { useAdminUnread } from "@/hooks/useAdminUnread";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { cn } from "@/lib/cn";
import { LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { BrandLogo } from "./BrandLogo";
import { Button } from "./ui/button";

type NavItem = { to: string; label: string; end?: boolean };

const tenantNav: NavItem[] = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/property", label: "Property" },
  { to: "/rent", label: "Rent" },
  { to: "/repairs", label: "Repairs" },
  { to: "/profile", label: "Profile" },
];

// Admins see admin-only sections in the top nav. Tenant-facing items
// (Property, Rent, the tenant Repairs view) are hidden because admins manage
// the portfolio rather than live in a single tenancy.
const adminNav: NavItem[] = [
  { to: "/admin/communications", label: "Inbox" },
  { to: "/admin/repairs", label: "Repairs" },
  { to: "/admin/approvals", label: "Approvals" },
  { to: "/admin/audit", label: "Audit" },
  { to: "/admin/properties", label: "Properties" },
  { to: "/admin/tenancies", label: "Tenancies" },
  { to: "/admin/tenants", label: "Tenants" },
  { to: "/admin/compliance", label: "Compliance" },
  { to: "/admin/contractors", label: "Contractors" },
  { to: "/admin/invites", label: "Invites" },
  { to: "/profile", label: "Profile" },
];

export function Layout() {
  const { signOut } = useAuth();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const unread = useAdminUnread();

  const items = isAdmin ? adminNav : tenantNav;

  return (
    <div className="min-h-dvh flex flex-col">
      <header className="border-b border-line">
        <div className="mx-auto max-w-5xl px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="text-ink no-underline">
            <BrandLogo />
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {items.map((item) => {
              const showBadge = item.to === "/admin/communications" && unread > 0;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={"end" in item ? item.end : false}
                  className={({ isActive }) =>
                    cn(
                      "px-3 h-9 inline-flex items-center gap-1.5 rounded-full text-sm transition",
                      isActive
                        ? "text-ink bg-white/5 border border-line"
                        : "text-ink-dim hover:text-ink",
                    )
                  }
                >
                  {item.label}
                  {showBadge ? (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
            <Button
              variant="ghost"
              size="sm"
              className="ml-2"
              onClick={async () => {
                await signOut();
                navigate("/login");
              }}
            >
              <LogOut size={14} />
              Sign out
            </Button>
          </nav>
          <button
            type="button"
            className="md:hidden text-ink p-2"
            aria-label="toggle navigation"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {open ? (
          <div className="md:hidden border-t border-line">
            <nav className="mx-auto max-w-5xl px-6 py-4 flex flex-col gap-1">
              {items.map((item) => {
                const showBadge = item.to === "/admin/communications" && unread > 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={"end" in item ? item.end : false}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "px-3 py-2 rounded-lg text-sm flex items-center gap-2",
                        isActive ? "text-ink bg-white/5" : "text-ink-dim hover:text-ink",
                      )
                    }
                  >
                    <span>{item.label}</span>
                    {showBadge ? (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                        {unread > 99 ? "99+" : unread}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
              <Button
                variant="ghost"
                size="sm"
                className="self-start mt-2"
                onClick={async () => {
                  await signOut();
                  navigate("/login");
                }}
              >
                <LogOut size={14} /> Sign out
              </Button>
            </nav>
          </div>
        ) : null}
      </header>
      <main className="flex-1">
        <div className="mx-auto max-w-5xl px-4 md:px-6 py-6 md:py-10">
          <Outlet />
        </div>
      </main>
      <footer className="border-t border-line">
        <div className="mx-auto max-w-5xl px-6 py-6 text-xs text-ink-dim">
          &copy; {new Date().getFullYear()} Trinium Property Management
        </div>
      </footer>
    </div>
  );
}
